import { useCallback, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type {
  Contact,
  ContactFormValues,
  ContactResponse,
  ContactType,
} from "../types";

interface UseSaveContactResult {
  isSaving: boolean;
  error: Error | null;
  saveContact: (
    values: ContactFormValues,
    contactId?: string,
  ) => Promise<Contact>;
}

export function useSaveContact(): UseSaveContactResult {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const saveContact = useCallback(
    async (values: ContactFormValues, contactId?: string) => {
      setIsSaving(true);
      setError(null);

      const payload = {
        taxId: values.taxId,
        name: values.name,
        tradeName: values.tradeName || null,
        contactType: values.contactType as ContactType,
        metadata: {
          giro: values.giro || null,
          businessActivity: values.giro || null,
          email: values.email || null,
          phone: values.phone || null,
          address: values.address || null,
          region: values.region || null,
          province: values.province || null,
          comuna: values.comuna || null,
          purchaseCondition: values.purchaseCondition || null,
          saleCondition: values.saleCondition || null,
          juridical: values.juridical,
          foreign: values.foreign,
        },
      };

      try {
        const data = contactId
          ? await apiFetch.put<ContactResponse>(
              `/api/contacts/${contactId}`,
              payload,
            )
          : await apiFetch.post<ContactResponse>("/api/contacts", payload);

        return data.contact;
      } catch (err) {
        const nextError = toError(err);
        setError(nextError);
        throw nextError;
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  return { isSaving, error, saveContact };
}

function toError(error: unknown): Error {
  if (error instanceof ApiError) return error;
  return error instanceof Error ? error : new Error("Unknown error");
}
